import React, { useState, useEffect, memo } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  TextField,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SetValue from './SetValue';
import { useHistory } from 'react-router-dom';
import Store from '../../../Store/Store';
import { set_combine_trends } from '../../../Action/Action';

// LED Gösterge Bileşeni - Component dışında tanımlandı
const LEDIndicator = memo(({ active, color = '#00ff00', inactiveColor = '#333' }) => (
  <Box
    sx={{
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: active ? color : inactiveColor,
      border: '2px solid #666',
      boxShadow: active ? `0 0 8px ${color}` : 'none'
    }}
  />
));

// Satır Bileşeni (Label + Value) - Component dışında tanımlandı
const PopupRow = memo(({ label, children, tagInfo, tagId, tagName, tagValue }) => {
  // Sağ tık ile tag bilgisi göster
  const handleContextMenu = (e) => {
    if (tagId) {
      e.preventDefault();
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent('openTagInfo', {
        detail: {
          tagId: tagId,
          tagName: tagName || `${label} - Tag_${tagId}`,
          currentValue: tagValue ?? null
        }
      }));
    }
  };

  return (
    <Box
      onContextMenu={handleContextMenu}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        borderBottom: '1px solid #e0e0e0',
        cursor: tagId ? 'context-menu' : 'default'
      }}
    >
      <Typography sx={{ width: 180, fontWeight: 500, color: '#333' }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        {children}
      </Box>
      {tagInfo && (
        <Typography sx={{ fontSize: 10, color: '#999', ml: 1 }}>
          {tagInfo}
        </Typography>
      )}
    </Box>
  );
});

// Sarı Input Box - Component dışında tanımlandı
const YellowInput = memo(({ value, unit, editable, onChange, disabled }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#edbc45',
    border: '2px solid #000',
    borderRadius: 1,
    px: 1,
    width: 135,
    height: 32
  }}>
    {editable ? (
      <TextField
        value={value}
        onChange={onChange}
        disabled={disabled}
        size="small"
        type="number"
        variant="standard"
        InputProps={{
          disableUnderline: true,
          sx: { fontSize: 14, fontWeight: 600 }
        }}
        sx={{ width: 60 }}
      />
    ) : (
      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{value}</Typography>
    )}
    {unit && (
      <Typography sx={{ ml: 0.5, fontSize: 14 }}>{unit}</Typography>
    )}
  </Box>
));

/**
 * VRFRoomWidget - VRF Oda Widget'ı (Company 1 Tasarımı)
 *
 * Popup İçeriği:
 * - ODA SICAKLIĞI (RoomTemp) - Okuma
 * - ODA SET DEĞERİ (SetTemp) - Okuma/Yazma + Kilit
 * - CİHAZ (OnOffState/OnOffSetup) - LED + ON/OFF + Kilit
 * - ÇALIŞMA MODU (OperationModeSetup) - Dropdown + Kilit
 * - ÇALIŞMA DURUMU (OperationModeState) - LED gösterge
 * - FAN MODU (FanSpeedState/FanSpeedSetup) - Dropdown
 * - ARIZA DURUMU (AlarmSignal) - Durum
 * - ARIZA KODU (ErrorCode) - Kod
 */
const VRFRoomWidget = ({
  id,
  text = 'ODA',
  width = 200,
  height = 100,

  // Tag ID'leri (elementDetails veya doğrudan)
  tagId = null,      // Oda sıcaklığı (RoomTemp)
  tagId2 = null,     // Set değeri (SetTemp)
  tagId3 = null,     // Oda sıcaklığı tekrar
  tagId4 = null,     // Set değeri tekrar
  tagId5 = null,     // ON/OFF kontrol (OnOffSetup)
  tagId6 = null,     // Çalışma modu durumu (OperationModeState)
  tagId7 = null,     // Çalışma modu kontrol (OperationModeSetup)
  tagId8 = null,     // Fan hızı durumu (FanSpeedState)
  tagId9 = null,     // Fan hızı kontrol (FanSpeedSetup)
  tagId10 = null,    // Alarm sinyali (AlarmSignal)
  tagId11 = null,    // Hata kodu (ErrorCode)
  tagId12 = null,    // Set değeri kilit (SetTempLock)
  tagId13 = null,    // ON/OFF kilit (OnOffLock)
  tagId14 = null,    // Çalışma modu kilit (OperationModeLock)
  tagId15 = null,    // Set sıcaklık soğutma (SetTempCool)
  tagId16 = null,    // Set sıcaklık ısıtma (SetTempHeat)

  // OnOff State için ayrı tag (LED için)
  onOffStateTagId = null,

  tagValue = null,
  tagValues = {},

  // Company ID (SetValue için gerekli)
  companyId = 4,

  // Görünüm
  backgroundColor = '#edbc45',
  foreColor = '#000000',
  borderColor = '#433316',
  borderWidth = 3,
  borderRadius = 16,
  fontSize = 16,

  // Durum renkleri
  activeColor = '#edbc45',
  inactiveColor = '#d3d3d3',
  coolingColor = '#6495ed',

  // elementDetails (DynPage uyumu)
  elementDetails = {},
  properties = {},

  isEditMode = false,
  onSetValue = null,

  ...props
}) => {
  const history = useHistory();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [setValueInput, setSetValueInput] = useState('');


  // Değişiklik takibi için local state'ler
  const [pendingChanges, setPendingChanges] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // elementDetails'ten değerleri al
  const roomName = elementDetails?.text || properties?.text || text;

  // Direct tag ID references for safe access (avoid eval)
  const directTagIds = {
    1: tagId,
    2: tagId2,
    3: tagId3,
    4: tagId4,
    5: tagId5,
    6: tagId6,
    7: tagId7,
    8: tagId8,
    9: tagId9,
    10: tagId10,
    11: tagId11,
    12: tagId12,
    13: tagId13,
    14: tagId14,
    15: tagId15,
    16: tagId16
  };

  // Tag ID'leri (öncelik: elementDetails > properties > props)
  const getTagId = (num) => {
    const key = num === 1 ? 'tagId' : `tagId${num}`;
    return elementDetails?.[key] || properties?.[key] || props[key] || directTagIds[num] || null;
  };

  const roomTempTagId = getTagId(1) || getTagId(3);  // RoomTemp
  const setTempTagId = getTagId(2) || getTagId(4);   // SetTemp
  const onOffSetupTagId = getTagId(5);               // OnOffSetup (yazma)
  const opModeStateTagId = getTagId(6);              // OperationModeState
  const opModeSetupTagId = getTagId(7);              // OperationModeSetup (yazma)
  const fanStateTagId = getTagId(8);                 // FanSpeedState
  const fanSetupTagId = getTagId(9);                 // FanSpeedSetup (yazma)
  const alarmTagId = getTagId(10);                   // AlarmSignal
  const errorTagId = getTagId(11);                   // ErrorCode
  const setTempLockTagId = getTagId(12);             // SetTempLock (kilit)
  const onOffLockTagId = getTagId(13);               // OnOffLock (kilit)
  const opModeLockTagId = getTagId(14);              // OperationModeLock (kilit)
  const setTempCoolTagId = getTagId(15);             // SetTempCool (soğutma set)
  const setTempHeatTagId = getTagId(16);             // SetTempHeat (ısıtma set)

  // OnOffStateTagId - önce props'tan, sonra elementDetails'dan
  const effectiveOnOffStateTagId = onOffStateTagId
    || elementDetails?.onOffStateTagId
    || properties?.onOffStateTagId
    || null;

  // OnOffSetup'tan OnOffState tag ID'sini türet (Company 4 pattern)
  // OnOffSetup → OnOffState derivation for Company 4 channels
  // Each channel has: (minSetupId, maxSetupId, minStateId, stateIncrement)
  const CHANNEL_PATTERNS = [
    { min: 15363, max: 15412, stateBase: 15213, stateInc: 3 },  // _(50)
    { min: 16245, max: 16294, stateBase: 16101, stateInc: 3 },  // _(51)
    { min: 17127, max: 17176, stateBase: 16977, stateInc: 3 },  // _(52)
    { min: 18009, max: 18058, stateBase: 17865, stateInc: 3 },  // _(53)
    { min: 18891, max: 18940, stateBase: 18741, stateInc: 3 },  // _(54)
    { min: 19755, max: 19804, stateBase: 19617, stateInc: 3 },  // _(55)
    { min: 23319, max: 23368, stateBase: 23169, stateInc: 3 },  // _(58)
    { min: 24222, max: 24271, stateBase: 24071, stateInc: 3 },  // _(59)
    { min: 24859, max: 24908, stateBase: 24799, stateInc: 1 },  // _(60)
    { min: 25417, max: 25466, stateBase: 25291, stateInc: 3 },  // _(61)
    { min: 27010, max: 27059, stateBase: 26875, stateInc: 3 },  // _(62)
    { min: 27847, max: 27896, stateBase: 27703, stateInc: 3 },  // _(63)
    { min: 28729, max: 28778, stateBase: 28579, stateInc: 3 },  // _(64)
  ];

  const deriveOnOffStateTagId = (setupTagId) => {
    if (!setupTagId) return null;
    const setupId = Number(setupTagId);

    for (const ch of CHANNEL_PATTERNS) {
      if (setupId >= ch.min && setupId <= ch.max) {
        return ch.stateBase + (setupId - ch.min) * ch.stateInc;
      }
    }
    return null;
  };

  // Tag değeri al
  const getTagValue = (tid) => {
    if (!tid) return null;
    const tagIdStr = String(tid);
    if (Array.isArray(tagValues)) {
      const found = tagValues.find(t => String(t.tagId) === tagIdStr);
      return found?.tagValue ?? found?.value ?? null;
    }
    // Handle object format from PreviewMode: { value, companyId, raw }
    const tagData = tagValues[tagIdStr];
    if (tagData && typeof tagData === 'object') {
      return tagData.value ?? tagData.raw ?? null;
    }
    return tagData ?? null;
  };

  // Mevcut değerler
  const roomTemp = getTagValue(roomTempTagId) ?? tagValue ?? '--';

  // OnOffState: önce explicit prop/elementDetails, sonra türetilmiş tag, en son Setup tag
  const derivedStateTagId = deriveOnOffStateTagId(onOffSetupTagId);
  const onOffState = getTagValue(effectiveOnOffStateTagId)
    ?? getTagValue(derivedStateTagId)
    ?? getTagValue(onOffSetupTagId)
    ?? '0';
  const opModeState = getTagValue(opModeStateTagId) ?? '0';
  const opModeSetup = getTagValue(opModeSetupTagId) ?? '0';

  // Aktif SetTemp tag'i: Mode 1 (Soğutma) → SetTempCool, Mode 2 (Isıtma) → SetTempHeat
  const getActiveSetTempTagId = () => {
    const mode = String(opModeState);
    if (mode === '1' && setTempCoolTagId) {
      return setTempCoolTagId;
    }
    if (mode === '2' && setTempHeatTagId) {
      return setTempHeatTagId;
    }
    return setTempTagId;
  };
  const activeSetTempTagId = getActiveSetTempTagId();

  // Set sıcaklık değeri: aktif moda göre oku (Soğutma→SetTempCool, Isıtma→SetTempHeat, diğer→SetTemp)
  const setTemp = getTagValue(activeSetTempTagId) ?? getTagValue(setTempTagId) ?? '--';

  const fanState = getTagValue(fanStateTagId) ?? '0';
  const alarmState = getTagValue(alarmTagId) ?? '0';
  const errorCode = getTagValue(errorTagId) ?? '0';

  // Kilit durumları - tag'den oku (1 = kilitli, 0 = açık)
  const setTempLockState = getTagValue(setTempLockTagId);
  const onOffLockState = getTagValue(onOffLockTagId);
  const opModeLockState = getTagValue(opModeLockTagId);

  // Kilit durumu kontrolü (tag varsa tag'den, yoksa varsayılan açık)
  const isSetTempLocked = setTempLockTagId ? (setTempLockState === '1' || setTempLockState === 1) : false;
  const isOnOffLocked = onOffLockTagId ? (onOffLockState === '1' || onOffLockState === 1) : false;
  const isOpModeLocked = opModeLockTagId ? (opModeLockState === '1' || opModeLockState === 1) : false;

  useEffect(() => {
    if (setTemp !== '--') {
      setSetValueInput(String(setTemp));
    }
  }, [setTemp]);

  // Cihaz durumu
  const isDeviceOn = onOffState === '1' || onOffState === 1;

  // Çalışma modu metni
  const getOpModeText = (val) => {
    const v = String(val);
    switch(v) {
      case '0': return 'KAPALI';
      case '1': return 'SOĞUTMA';
      case '2': return 'ISITMA';
      case '3': return 'FAN';
      case '4': return 'AUTO';
      default: return 'BİLİNMİYOR';
    }
  };

  // Fan modu metni
  const getFanModeText = (val) => {
    const v = String(val);
    switch(v) {
      case '1': return 'DÜŞÜK';
      case '2': return 'YÜKSEK';
      case '4': return 'ORTA';
      case '5': return 'AUTO';
      default: return v;
    }
  };

  // Alarm durumu metni
  const getAlarmText = (val) => {
    return val === '1' || val === 1 ? 'ALARM' : 'NORMAL';
  };

  // Widget arka plan rengi: çalışıyorsa sarı, çalışmıyorsa gri
  const getBackgroundColor = () => {
    if (alarmState === '1' || alarmState === 1) return '#ff0000'; // Alarm - kırmızı
    if (!isDeviceOn) return inactiveColor; // Kapalı - gri
    return activeColor; // Açık - sarı
  };

  const handleOpen = () => {
    if (isEditMode) return;
    // Dialog açıldığında pending changes'ı temizle
    setPendingChanges({});
    setHasChanges(false);
    setDialogOpen(true);
  };

  const handleClose = () => {
    // Değişiklikler varsa uyar veya temizle
    setPendingChanges({});
    setHasChanges(false);
    setDialogOpen(false);
  };

  // Değişiklik ekle (hemen göndermez, pending'e ekler)
  const addPendingChange = (tagId, value, label) => {
    if (!tagId) return;
    setPendingChanges(prev => ({
      ...prev,
      [tagId]: { value, label }
    }));
    setHasChanges(true);
  };

  // Cihaz ON/OFF değişikliği
  const handleDeviceToggle = (val) => {
    if (!onOffSetupTagId) return;
    addPendingChange(onOffSetupTagId, val, 'Cihaz');
  };

  // Çalışma modu değişikliği
  const handleOpModeChange = (val) => {
    if (!opModeSetupTagId) return;
    addPendingChange(opModeSetupTagId, val, 'Çalışma Modu');
  };

  // Fan modu değişikliği
  const handleFanModeChange = (val) => {
    if (!fanSetupTagId) return;
    addPendingChange(fanSetupTagId, val, 'Fan Modu');
  };

  // Kilit toggle fonksiyonları - sahaya gönderir
  const handleSetTempLockToggle = () => {
    if (!setTempLockTagId) return;
    const newValue = isSetTempLocked ? '0' : '1';
    addPendingChange(setTempLockTagId, newValue, 'Set Değeri Kilit');
  };

  const handleOnOffLockToggle = () => {
    if (!onOffLockTagId) return;
    const newValue = isOnOffLocked ? '0' : '1';
    addPendingChange(onOffLockTagId, newValue, 'Cihaz Kilit');
  };

  const handleOpModeLockToggle = () => {
    if (!opModeLockTagId) return;
    const newValue = isOpModeLocked ? '0' : '1';
    addPendingChange(opModeLockTagId, newValue, 'Çalışma Modu Kilit');
  };

  // Tüm değişiklikleri kaydet
  const handleSaveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) return;

    try {
      // Tüm değişiklikleri sırayla gönder
      for (const [tagId, { value }] of Object.entries(pendingChanges)) {
        if (onSetValue) {
          await onSetValue({ tagId: parseInt(tagId), value });
        } else {
          // onSetValue yoksa doğrudan API çağrısı yap
          console.log('VRFRoomWidget: Sending SetValue API call', { tagId, value, companyId });
          try {
            const response = await fetch('https://app-rmmscmdapi.azurewebsites.net/api/cmd/SetValue', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                TagId: String(tagId),
                TagValue: String(value),
                CompanyId: String(companyId)
              })
            });
            const result = await response.json();
            console.log('VRFRoomWidget: SetValue API response', result);

            // Dispatch event to trigger delayed tag refresh
            window.dispatchEvent(new CustomEvent('tag-value-updated', {
              detail: { tagId, value, timestamp: Date.now() }
            }));
            console.log('VRFRoomWidget: Dispatched tag-value-updated event');
          } catch (apiError) {
            console.error('VRFRoomWidget: SetValue API error', apiError);
          }
        }
      }
    } catch (e) {
      console.error('Save changes error:', e);
    } finally {
      // Her durumda temizle (başarılı veya hata olsa da)
      setPendingChanges({});
      setHasChanges(false);
    }
  };

  // Değişiklikleri iptal et
  const handleCancelChanges = () => {
    setPendingChanges({});
    setHasChanges(false);
  };

  // Kombine Trend - Raporlama sayfasına yönlendir
  const handleKombineTrend = () => {
    // Tag bilgilerini topla - VRF1.js ile aynı format
    const tagidsData = [];

    if (roomTempTagId) {
      tagidsData.push({ tagName: `${roomName} - Oda Sıcaklığı`, tagId: roomTempTagId });
    }
    if (setTempTagId) {
      tagidsData.push({ tagName: `${roomName} - Set Değeri`, tagId: setTempTagId });
    }
    if (opModeStateTagId) {
      tagidsData.push({ tagName: `${roomName} - Çalışma Durumu`, tagId: opModeStateTagId });
    }
    if (fanStateTagId) {
      tagidsData.push({ tagName: `${roomName} - Fan Durumu`, tagId: fanStateTagId });
    }
    if (alarmTagId) {
      tagidsData.push({ tagName: `${roomName} - Arıza Durumu`, tagId: alarmTagId });
    }

    console.log('Kombine Trend - Tag Data:', tagidsData);

    // Redux store'a dispatch et ve raporlama sayfasına yönlendir
    if (tagidsData.length > 0) {
      Store.dispatch(set_combine_trends(tagidsData));
      setDialogOpen(false);
      history.push('/raporlama');
    } else {
      console.warn('Kombine Trend: No tags found for this room');
    }
  };


  // Çalışma modu LED rengi
  const getOperationModeLedColor = () => {
    const mode = String(opModeState);
    if (mode === '1') return '#6495ed'; // Soğutma - mavi
    if (mode === '2') return '#ff0000'; // Isıtma - kırmızı
    return '#808080'; // Kapalı/Diğer - gri
  };

  return (
    <>
      {/* Ana Widget Kutusu - Company 1 Tasarımı */}
      <Box
        onClick={handleOpen}
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: getBackgroundColor(),
          border: `${borderWidth}px solid ${borderColor}`,
          borderRadius: `${borderRadius}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isEditMode ? 'default' : 'pointer',
          transition: 'all 0.3s ease',
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          '&:hover': {
            opacity: isEditMode ? 1 : 0.9,
            transform: isEditMode ? 'none' : 'scale(1.02)'
          },
          padding: '8px',
          boxSizing: 'border-box',
          position: 'relative'
        }}
      >
        {/* Oda Adı - Üstte */}
        <Typography
          sx={{
            color: 'rgb(45, 45, 45)',
            fontSize: `${fontSize}px`,
            fontWeight: 600,
            textAlign: 'center',
            mb: 0.5
          }}
        >
          {roomName}
        </Typography>

        {/* Alt Satır: ODA Sıcaklık | LED | SET Değer */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1
        }}>
          {/* ODA Sıcaklığı */}
          <Box sx={{
            border: '1px solid #333',
            borderRadius: '4px',
            px: 0.8,
            py: 0.3,
            backgroundColor: 'rgba(255,255,255,0.3)',
            fontSize: '11px',
            fontWeight: 600,
            whiteSpace: 'nowrap'
          }}>
            ODA: {roomTemp !== '--' ? `${roomTemp}°C` : '--'}
          </Box>

          {/* LED Indicator - Çalışma Modu */}
          <Box sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            backgroundColor: getOperationModeLedColor(),
            border: '2px solid #333',
            flexShrink: 0,
            boxShadow: isDeviceOn ? `0 0 6px ${getOperationModeLedColor()}` : 'none'
          }} />

          {/* SET Değeri */}
          <Box sx={{
            border: '1px solid #333',
            borderRadius: '4px',
            px: 0.8,
            py: 0.3,
            backgroundColor: 'rgba(255,255,255,0.3)',
            fontSize: '11px',
            fontWeight: 600,
            whiteSpace: 'nowrap'
          }}>
            SET: {setTemp !== '--' ? setTemp : '--'}
          </Box>
        </Box>
      </Box>

      {/* Popup Dialog - Company 1 Tasarımı */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: '3px solid #edbc45',
            maxWidth: 530,
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            margin: 0,
            minHeight: 580,
            maxHeight: '90vh'
          }
        }}
      >
        {/* Başlık */}
        <DialogTitle sx={{
          textAlign: 'center',
          fontSize: 24,
          fontWeight: 600,
          py: 2,
          borderBottom: '1px solid #e0e0e0',
          position: 'relative'
        }}>
          {roomName}
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, overflowY: 'auto', overflowX: 'hidden', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {/* ODA SICAKLIĞI */}
          <PopupRow label="ODA SICAKLIĞI" tagId={roomTempTagId} tagName={`${roomName} - Oda Sıcaklığı`} tagValue={roomTemp}>
            <Box sx={{ width: 28 }} /> {/* LED hizalama boşluğu */}
            <YellowInput value={roomTemp} unit="°C" />
          </PopupRow>

          {/* ODA SET DEĞERİ - SetValue widget kullanılıyor */}
          <PopupRow label="ODA SET DEĞERİ" tagId={setTempTagId} tagName={`${roomName} - Set Değeri`} tagValue={setTemp}>
            <Box sx={{ width: 28 }} /> {/* LED hizalama boşluğu */}
            <Box
              sx={{ width: 142, height: 25, position: 'relative', zIndex: 10, mb: '10px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <SetValue
                tagId={activeSetTempTagId}
                tagValue={setTemp}
                unit="°C"
                placeholder="--"
                buttonText="Ayarla"
                backgroundColor="#edbc45"
                borderColor="#000"
                borderWidth={2}
                borderRadius={4}
                fontSize={14}
                fontWeight={600}
                companyId={companyId}
                height={25}
              />
            </Box>
            <IconButton
              size="small"
              onClick={handleSetTempLockToggle}
              disabled={!setTempLockTagId}
              sx={{
                ml: 1,
                color: !setTempLockTagId ? '#ccc' : (pendingChanges[setTempLockTagId] ? '#4caf50' : (isSetTempLocked ? '#f44336' : '#666'))
              }}
            >
              {(pendingChanges[setTempLockTagId]?.value === '1' || (!pendingChanges[setTempLockTagId] && isSetTempLocked))
                ? <LockIcon fontSize="small" />
                : <LockOpenIcon fontSize="small" />}
            </IconButton>
          </PopupRow>

          {/* CİHAZ */}
          <PopupRow label="CİHAZ" tagId={effectiveOnOffStateTagId || onOffSetupTagId} tagName={`${roomName} - Cihaz ON/OFF`} tagValue={onOffState}>
            <LEDIndicator active={isDeviceOn} color="#00ff00" />
            <Box
              component="select"
              value={pendingChanges[onOffSetupTagId]?.value ?? (isDeviceOn ? '1' : '0')}
              disabled={isOnOffLocked && !pendingChanges[onOffLockTagId]}
              onChange={(e) => handleDeviceToggle(e.target.value)}
              sx={{
                ml: 1,
                width: 110,
                height: 32,
                backgroundColor: pendingChanges[onOffSetupTagId] ? '#90EE90' : '#edbc45',
                border: pendingChanges[onOffSetupTagId] ? '2px solid #4caf50' : '2px solid #000',
                borderRadius: '4px',
                px: 1.5,
                fontSize: 14,
                fontWeight: 600,
                textAlign: 'center',
                cursor: (isOnOffLocked && !pendingChanges[onOffLockTagId]) ? 'not-allowed' : 'pointer',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23333\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                backgroundSize: '16px',
                paddingRight: '32px',
                '&:hover': {
                  backgroundColor: pendingChanges[onOffSetupTagId] ? '#7CFC00' : '#d4a93d'
                },
                '&:focus': { outline: '2px solid #1976d2' }
              }}
            >
              <option value="0">OFF</option>
              <option value="1">ON</option>
            </Box>
            <IconButton
              size="small"
              onClick={handleOnOffLockToggle}
              disabled={!onOffLockTagId}
              sx={{
                color: !onOffLockTagId ? '#ccc' : (pendingChanges[onOffLockTagId] ? '#4caf50' : (isOnOffLocked ? '#f44336' : '#666'))
              }}
            >
              {(pendingChanges[onOffLockTagId]?.value === '1' || (!pendingChanges[onOffLockTagId] && isOnOffLocked))
                ? <LockIcon fontSize="small" />
                : <LockOpenIcon fontSize="small" />}
            </IconButton>
          </PopupRow>

          {/* ÇALIŞMA MODU */}
          <PopupRow label="ÇALIŞMA MODU" tagId={opModeSetupTagId} tagName={`${roomName} - Çalışma Modu`} tagValue={opModeSetup}>
            <Box sx={{ width: 28 }} /> {/* LED hizalama boşluğu */}
            <Box
              component="select"
              value={pendingChanges[opModeSetupTagId]?.value ?? String(opModeSetup)}
              disabled={isOpModeLocked && !pendingChanges[opModeLockTagId]}
              onChange={(e) => handleOpModeChange(e.target.value)}
              sx={{
                width: 110,
                height: 32,
                backgroundColor: pendingChanges[opModeSetupTagId] ? '#90EE90' : '#edbc45',
                border: pendingChanges[opModeSetupTagId] ? '2px solid #4caf50' : '2px solid #000',
                borderRadius: '4px',
                px: 1.5,
                fontSize: 14,
                fontWeight: 600,
                textAlign: 'center',
                cursor: (isOpModeLocked && !pendingChanges[opModeLockTagId]) ? 'not-allowed' : 'pointer',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23333\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                backgroundSize: '16px',
                paddingRight: '32px',
                '&:hover': {
                  backgroundColor: pendingChanges[opModeSetupTagId] ? '#7CFC00' : '#d4a93d'
                },
                '&:focus': { outline: '2px solid #1976d2' }
              }}
            >
              <option value="0">NEM ALMA</option>
              <option value="1">SOĞUTMA</option>
              <option value="2">ISITMA</option>
              <option value="3">FAN</option>
              <option value="4">AUTO</option>
            </Box>
            <IconButton
              size="small"
              onClick={handleOpModeLockToggle}
              disabled={!opModeLockTagId}
              sx={{
                color: !opModeLockTagId ? '#ccc' : (pendingChanges[opModeLockTagId] ? '#4caf50' : (isOpModeLocked ? '#f44336' : '#666'))
              }}
            >
              {(pendingChanges[opModeLockTagId]?.value === '1' || (!pendingChanges[opModeLockTagId] && isOpModeLocked))
                ? <LockIcon fontSize="small" />
                : <LockOpenIcon fontSize="small" />}
            </IconButton>
          </PopupRow>

          {/* ÇALIŞMA DURUMU */}
          <PopupRow label="ÇALIŞMA DURUMU" tagId={opModeStateTagId} tagName={`${roomName} - Çalışma Durumu`} tagValue={opModeState}>
            <LEDIndicator
              active={opModeState === '1' || opModeState === 1 || opModeState === '2' || opModeState === 2}
              color={getOperationModeLedColor()}
            />
            <Typography sx={{ ml: 1, fontSize: 14 }}>
              {getOpModeText(opModeState)}
            </Typography>
          </PopupRow>

          {/* FAN MODU */}
          <PopupRow label="FAN MODU" tagId={fanSetupTagId} tagName={`${roomName} - Fan Modu`} tagValue={fanState}>
            <Box sx={{ width: 28 }} /> {/* LED hizalama boşluğu */}
            <Box
              component="select"
              value={pendingChanges[fanSetupTagId]?.value ?? String(fanState)}
              onChange={(e) => handleFanModeChange(e.target.value)}
              sx={{
                width: 110,
                height: 32,
                backgroundColor: pendingChanges[fanSetupTagId] ? '#90EE90' : '#edbc45',
                border: pendingChanges[fanSetupTagId] ? '2px solid #4caf50' : '2px solid #000',
                borderRadius: '4px',
                px: 1.5,
                fontSize: 14,
                fontWeight: 600,
                textAlign: 'center',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23333\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                backgroundSize: '16px',
                paddingRight: '32px',
                '&:hover': {
                  backgroundColor: pendingChanges[fanSetupTagId] ? '#7CFC00' : '#d4a93d'
                },
                '&:focus': { outline: '2px solid #1976d2' }
              }}
            >
              <option value="5">AUTO</option>
              <option value="1">DÜŞÜK</option>
              <option value="4">ORTA</option>
              <option value="2">YÜKSEK</option>
            </Box>
          </PopupRow>

          {/* ARIZA DURUMU */}
          <PopupRow label="ARIZA DURUMU" tagId={alarmTagId} tagName={`${roomName} - Arıza Durumu`} tagValue={alarmState}>
            <Box sx={{ width: 28 }} /> {/* LED hizalama boşluğu */}
            <Box sx={{
              backgroundColor: alarmState === '1' ? '#ff0000' : '#edbc45',
              color: alarmState === '1' ? '#fff' : '#000',
              border: '2px solid #000',
              px: 2,
              borderRadius: 1,
              fontWeight: 600,
              height: 32,
              width: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {getAlarmText(alarmState)}
            </Box>
          </PopupRow>

          {/* ARIZA KODU */}
          <PopupRow label="ARIZA KODU" tagId={errorTagId} tagName={`${roomName} - Arıza Kodu`} tagValue={errorCode}>
            <Box sx={{ width: 28 }} /> {/* LED hizalama boşluğu */}
            <Box sx={{
              backgroundColor: '#edbc45',
              border: '2px solid #000',
              px: 2,
              borderRadius: 1,
              fontWeight: 600,
              height: 32,
              width: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {errorCode === '0' ? 'NORMAL' : `HATA: ${errorCode}`}
            </Box>
          </PopupRow>

          {/* KOMBİNE TREND butonu */}
          <Button
            variant="contained"
            startIcon={<TrendingUpIcon />}
            onClick={handleKombineTrend}
            sx={{
              mt: 2,
              width: 'calc(100% - 50px)',
              mx: 'auto',
              display: 'block',
              backgroundColor: '#edbc45',
              color: '#000',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#d4a93d' }
            }}
          >
            KOMBİNE TREND
          </Button>

        </DialogContent>

        {/* Kaydet / İptal Butonları - Değişiklik varsa göster (DialogActions ile sabit alt kısımda) */}
        {hasChanges && (
          <DialogActions sx={{
            justifyContent: 'center',
            gap: 2,
            py: 2,
            borderTop: '2px solid #edbc45',
            backgroundColor: '#fff'
          }}>
            <Button
              variant="contained"
              onClick={handleSaveChanges}
              sx={{
                backgroundColor: '#4caf50',
                color: '#fff',
                fontWeight: 600,
                px: 4,
                '&:hover': { backgroundColor: '#388e3c' }
              }}
            >
              KAYDET
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancelChanges}
              sx={{
                borderColor: '#f44336',
                color: '#f44336',
                fontWeight: 600,
                px: 4,
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  borderColor: '#f44336'
                }
              }}
            >
              İPTAL
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default VRFRoomWidget;
